<template>
  <div
    class="relative bg-giveaway-bg bg-fixed bg-cover bg-center bg-no-repeat bg-[#00284F] py-[80px] px-2 sm:px-10 h-[5000px]"
  >
    <!-- Content -->
    <div
      class="relative z-10 flex flex-col items-center text-white text-center py-12 px-4 sm:px-6"
    >
      <!-- Title -->
      <h1
        v-motion-pop-visible
        class="text-5xl sm:text-[70px] font-black uppercase font-golos"
      >
        GIVEAWAYS
      </h1>

      <p v-motion-pop-visible class="font-montserrat mt-4 text-lg sm:text-3xl">
        CLAIM YOUR REWARDS
      </p>

      <h1
        v-motion-pop-visible
        class="flex justify-center items-center font-orbitron text-3xl sm:text-6xl font-black mt-12"
      >
        $ {{ animatedTotalGiveaway }}
      </h1>
      <h2
        v-motion-pop-visible
        class="text-lg sm:text-3xl lg:text-2xl font-montserrat mt-2"
      >
        TOTAL GIVEN AWAY
      </h2>
    </div>

    <!-- Centered search + filter with spacing between -->
    <div class="relative flex justify-center z-30 w-full mt-10 px-4 lg:px-0">
      <div
        v-motion-pop-visible
        class="w-full max-w-6xl rounded-lg p-0 flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4 select-none"
      >
        <!-- Desktop Search Bar & Filter Options -->
        <div class="hidden lg:flex items-center gap-2 mt-6 sm:mt-0">
          <!-- Search Bar -->
          <div
            class="relative flex items-center bg-transparent border border-white rounded-xl px-4 py-4 w-5 sm:w-64"
          >
            <svg
              class="w-5 h-5 text-white mr-2"
              fill="none"
              stroke="white"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M21 21l-4.35-4.35m2.35-6.65a7 7 0 1 1-14 0 7 7 0 0 1 14 0z"
              />
            </svg>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search..."
              class="bg-transparent lg:text-sm xl:text-base text-white placeholder-white focus:outline-none w-full"
            />
          </div>

          <!-- Category Dropdown -->
          <div class="relative">
            <div
              @click="toggleCategoryDropdown"
              class="flex items-center justify-between bg-transparent lg:text-sm xl:text-base lg:w-[160px] xl:w-[180px] border border-white rounded-xl px-4 py-4 cursor-pointer select-none relative"
            >
              <span class="text-white">{{ selectedCategory }}</span>
              <svg
                class="w-4 h-4 text-white ml-6 transition-transform"
                :class="{ 'rotate-180': showCategoryDropdown }"
                fill="none"
                stroke="white"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            <!-- Dropdown Options -->
            <transition name="fade">
              <div
                v-if="showCategoryDropdown"
                class="absolute top-14 left-0 bg-[#1D1D1D] border border-white rounded-lg shadow-lg lg:w-[160px] xl:w-[180px]"
              >
                <div
                  v-for="category in categories"
                  :key="category"
                  @click="applyCategory(category)"
                  class="px-4 py-3 hover:bg-[#302f2f] cursor-pointer transition-all"
                  :class="{
                    'text-white bg-[#302f2f]': category === selectedCategory,
                    'text-[#B0B0B0]': category !== selectedCategory,
                  }"
                >
                  {{ category }}
                </div>
              </div>
            </transition>
          </div>

          <!-- Sort Dropdown -->
          <div class="relative">
            <div
              @click="toggleSortDropdown"
              class="flex items-center justify-between bg-transparent lg:text-sm xl:text-base lg:w-[240px] xl:w-[260px] border border-white rounded-xl px-4 py-4 cursor-pointer select-none relative"
            >
              <span class="text-white">{{ selectedSortOption }}</span>
              <svg
                class="w-4 h-4 text-white ml-6 transition-transform"
                :class="{ 'rotate-180': showSortDropdown }"
                fill="none"
                stroke="white"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            <!-- Sort Options Dropdown -->
            <transition name="fade">
              <div
                v-if="showSortDropdown"
                class="absolute top-14 left-0 bg-[#1D1D1D] border border-white rounded-lg shadow-lg lg:w-[240px] xl:w-[260px]"
              >
                <div
                  v-for="option in sortOptions"
                  :key="option.value"
                  @click="applySort(option)"
                  class="px-4 py-3 hover:bg-[#302f2f] cursor-pointer transition-all rounded-b-xl"
                  :class="{
                    'text-white bg-[#302f2f] rounded-t-xl':
                      option.label === selectedSortOption,
                    'text-[#B0B0B0]': option.label !== selectedSortOption,
                  }"
                >
                  {{ option.label }}
                </div>
              </div>
            </transition>
          </div>
        </div>

        <!-- Purchase History Button -->
        <button
          @click="refreshData"
          :disabled="loading"
          class="hidden lg:flex justify-center items-center bg-[#1294F0] text-sm text-white lg:text-sm xl:text-base lg:w-[140px] xl:w-[200px] px-6 py-4 rounded-lg font-semibold hover:opacity-90 mt-6 sm:mt-0 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Refresh
        </button>
      </div>
    </div>

    <!-- Disclaimer Search Bar Mobile-->

    <div
      class="relative flex lg:hidden items-center bg-transparent w-full border border-white rounded-xl px-4 py-4"
    >
      <!-- Search Icon (SVG) -->
      <svg
        class="w-5 h-5 text-white mr-2"
        fill="none"
        stroke="white"
        stroke-width="2"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M21 21l-4.35-4.35m2.35-6.65a7 7 0 1 1-14 0 7 7 0 0 1 14 0z"
        />
      </svg>
      <!-- Input Field -->
      <input
        type="text"
        v-model="searchQuery"
        placeholder="Search..."
        class="bg-transparent text-white placeholder-white focus:outline-none w-full"
      />
    </div>

    <div class="relative z-30 flex lg:hidden justify-between items-center w-full mt-6">
      <!-- Sort Dropdown -->
      <div class="relative w-full">
        <div
          @click="toggleSortDropdown"
          class="flex items-center justify-between bg-transparent lg:text-sm xl:text-base w-full lg:w-[200px] xl:w-[260px] border border-white rounded-xl px-4 py-4 cursor-pointer select-none"
        >
          <span class="text-white">{{ selectedSortOption }}</span>
          <!-- Dropdown Arrow -->
          <svg
            class="w-4 h-4 text-white ml-6 transition-transform"
            :class="{ 'rotate-180': showSortDropdown }"
            fill="none"
            stroke="white"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        <!-- Sort Options Dropdown -->
        <transition name="fade">
          <div
            v-if="showSortDropdown"
            class="absolute top-14 left-0 bg-[#1D1D1D] border border-white rounded-xl shadow-lg w-full"
          >
            <div
              v-for="option in sortOptions"
              :key="option.value"
              @click="applySort(option)"
              class="px-4 py-3 hover:bg-[#302f2f] cursor-pointer transition-all rounded-b-xl"
              :class="{
                'text-white bg-[#302f2f] rounded-t-xl':
                  option.label === selectedSortOption,
                'text-[#B0B0B0]': option.label !== selectedSortOption,
              }"
            >
              {{ option.label }}
            </div>
          </div>
        </transition>
      </div>
      <div class="hidden sm:flex justify-end items-center w-full">
        <!-- Purchase History Button -->
        <button
          @click="refreshData"
          class="bg-[#1294F0] text-sm text-white px-6 py-4 rounded-lg font-semibold hover:opacity-90"
        >
          Refresh
        </button>
      </div>
    </div>

    <div class="flex sm:hidden justify-end items-center w-full mt-10">
      <!-- Purchase History Button -->
      <button
        @click="refreshData"
        class="bg-[#1294F0] text-sm text-white px-6 py-4 rounded-lg font-semibold hover:opacity-90"
      >
        Refresh
      </button>
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="flex justify-center items-center min-h-[300px]">
      <span
        class="animate-spin border-2 border-white border-t-transparent rounded-full w-12 h-12"
      ></span>
    </div>

    <div v-else class="flex flex-col items-center justify-center w-full px-4">
      <!-- Store Cards -->
      <!-- Giveaway Cards -->
      <div
        class="relative z-0 grid w-full max-w-6xl grid-cols-1 gap-6 mt-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      >
        <div
          v-for="giveaway in paginatedGiveaways"
          :key="giveaway._id"
          class="bg-[#262626] py-6 px-4 rounded-lg flex flex-col text-center shadow-lg h-full"
        >
          <!-- Gift Image -->
          <div class="w-24 h-24 sm:w-28 sm:h-28 mx-auto">
            <img
              :src="
                giveaway.imageUrl ? giveaway.imageUrl : require('../assets/gift-blue.png')
              "
              alt="Gift"
              class="w-full h-full object-contain aspect-square"
            />
          </div>

          <!-- Title & Description Container -->
          <div class="mt-4 text-white flex flex-col flex-grow items-center text-center">
            <!-- giveaway Title -->
            <h3 class="font-bold text-sm capitalize line-clamp-3 w-full">
              {{ giveaway.title }}
            </h3>
          </div>

          <!-- Join Giveaway Button -->
          <button
            @click="handleGiveawayClick(giveaway, joinedGiveawayIds.has(giveaway._id))"
            :disabled="!isUserLoggedIn || giveaway.status === 'active'"
            class="mt-4 px-6 py-2 text-lg sm:text-base bg-[#1294F0] text-white font-bold rounded-full hover:bg-[#1293f0b6] transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{
              giveaway.status === "started"
                ? joinedGiveawayIds.has(giveaway._id)
                  ? "Joined"
                  : "Join Giveaway"
                : giveaway.status === "active"
                ? "Waiting to start"
                : giveaway.status === "stopped"
                ? "View Entries"
                : giveaway.status === "ended"
                ? "View Winners"
                : ""
            }}
          </button>
        </div>
      </div>

      <!-- Pagination -->
      <div class="w-full max-w-6xl mt-10 border-2 border-[#363636] rounded-lg">
        <div
          v-motion-pop-visible
          class="bg-gradient-to-r from-[#1D1D1D] to-[#313B44] rounded-lg p-4 flex items-center justify-between select-none"
        >
          <!-- Left Section: Page Info -->
          <div class="flex items-center gap-2">
            <img
              src="@/assets/search.webp"
              alt="Search"
              class="w-10 h-10 sm:w-14 sm:h-14 object-contain"
            />
            <span class="text-white font-bold text-base sm:text-lg font-montserrat">
              {{ itemStart }} - {{ itemEnd }} of {{ totalItems }}
              Items
            </span>
          </div>

          <!-- Pagination Controls -->
          <div class="flex items-center gap-2">
            <!-- Previous Button -->
            <button
              class="p-2 bg-white rounded-md disabled:opacity-50"
              :disabled="currentPage === 1"
              @click="prevPage"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-5 h-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="#161E22"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <!-- Next Button -->
            <button
              class="p-2 bg-white rounded-md disabled:opacity-50"
              :disabled="currentPage === totalPages"
              @click="nextPage"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-5 h-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="#161E22"
                stroke-width="2"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <GiveawayDetailsModal
    :showGiveawayDetailsModal="showGiveawayDetailsModal"
    :giveawayDetails="giveawayDetails"
    @close="showGiveawayDetailsModal = false"
  />

  <GiveawayConfirmationModal
    :showGiveawayConfirmationModal="showGiveawayConfirmationModal"
    :giveawayDetails="giveawayDetails"
    @close="showGiveawayConfirmationModal = false"
    @joined-successfully="handleJoinSuccess"
  />

  <GiveawaySuccessModal
    :showGiveawaySuccessModal="showGiveawaySuccessModal"
    :giveawayTitle="joinedGiveawayTitle"
    @closeSuccessModal="showGiveawaySuccessModal = false"
  />
</template>

<script>
import GiveawayDetailsModal from "@/components/GiveawayDetailsModal.vue";
import GiveawayConfirmationModal from "@/components/GiveawayConfirmationModal.vue";
import GiveawaySuccessModal from "@/components/GiveawaySuccessModal.vue";
import axios from "axios";

export default {
  data() {
    return {
      loading: false,
      searchQuery: "",
      showDesktopFilter: false,
      showMobileFilter: false,
      totalGiveaway: 0,
      animatedTotalGiveaway: 0,
      animatedTotalItems: 0,
      giveaways: [],
      giveawayDetails: {},
      showSortDropdown: false,
      selectedSortOption: "From A to Z",
      sortOptions: [
        { label: "From A to Z", value: "aToZ" },
        { label: "From Z to A", value: "zToA" },
      ],
      categories: ["All", "Waiting", "Started", "Stopped", "Ended"],
      selectedCategory: "All",
      showCategoryDropdown: false,
      currentPage: 1,
      itemsPerPage: 8,
      showErrorModal: false,
      joinedGiveawayTitle: "",
      errorMessage: "",
      showSuccessModal: false,
      showConfirmModal: false,
      showRedirectModal: false,
      showGiveawayDetailsModal: false,
      showGiveawayConfirmationModal: false,
      showGiveawaySuccessModal: false,
      joinedGiveawayIds: new Set(),
    };
  },
  watch: {
    selectedSortOption(newVal) {
      this.selectedSortLabel = newVal;
    },
    totalItems(newValue) {
      this.animateNumber("animatedTotalItems", newValue);
    },
  },
  components: {
    GiveawayDetailsModal,
    GiveawayConfirmationModal,
    GiveawaySuccessModal,
  },
  computed: {
    filteredGiveaways() {
      let filtered = [...this.giveaways];
      if (this.searchQuery) {
        filtered = filtered.filter((giveaway) =>
          giveaway.title.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }
      if (this.selectedCategory.toLowerCase() !== "all") {
        filtered = filtered.filter((giveaway) => {
          const status = giveaway.status?.toLowerCase();
          if (this.selectedCategory.toLowerCase() === "waiting") {
            return status === "active";
          }
          return status === this.selectedCategory.toLowerCase();
        });
      }
      return filtered;
    },
    selectedSortLabel() {
      return this.selectedSortOption;
    },
    isUserLoggedIn() {
      return this.$store.state.user !== null && this.$store.state.isVerified;
    },
    paginatedGiveaways() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredGiveaways.slice(start, start + this.itemsPerPage);
    },
    totalItems() {
      return this.filteredGiveaways.length;
    },
    totalPages() {
      return Math.ceil(this.filteredGiveaways.length / this.itemsPerPage);
    },
    itemStart() {
      return this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    itemEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    },
    visiblePages() {
      const range = [];
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(this.totalPages, this.currentPage + 2);
      for (let i = start; i <= end; i++) {
        range.push(i);
      }
      return range;
    },
  },
  methods: {
    async init() {
      const baseUrl = process.env.VUE_APP_BASE_API;
      this.loading = true;
      try {
        const totalGiveawayResponse = await axios.get(`${baseUrl}/api/giveaways/total`, {
          headers: {
            "x-api-key": process.env.VUE_APP_X_API_KEY,
          },
        });
        if (totalGiveawayResponse.data) {
          this.totalGiveaway = totalGiveawayResponse.data.totalGiveaway;
          this.animateValues();
        }
      } catch (error) {
        console.error("Error loading data", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchGiveaways() {
      this.loading = true;
      const baseUrl = process.env.VUE_APP_BASE_API;

      try {
        const response = await axios.get(`${baseUrl}/api/giveaways`, {
          headers: {
            "x-api-key": process.env.VUE_APP_X_API_KEY,
          },
        });

        this.giveaways = response.data.giveways || [];

        // ✅ Check if the user joined each giveaway
        const token = localStorage.getItem("ballyboy-access-token");
        const username = this.$store?.state?.username || null;

        if (token && username) {
          await Promise.all(this.giveaways.map((g) => this.checkIfUserJoined(g._id)));
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        this.loading = false;
      }
    },
    async refreshData() {
      this.loading = true;
      try {
        this.totalGiveaway = 0;
        this.animatedTotalGiveaway = 0;
        await this.init();
        await this.fetchGiveaways();
      } catch (error) {
        console.error("Error refreshing data:", error);
      } finally {
        this.loading = false;
      }
    },
    async checkIfUserJoined(giveawayId) {
      const token = localStorage.getItem("ballyboy-access-token");
      const username = this.$store?.state?.username || null;

      if (token && username) {
        try {
          const baseUrl = process.env.VUE_APP_BASE_API;
          const res = await axios.get(`${baseUrl}/api/giveaways/${giveawayId}/users`, {
            headers: {
              "x-api-key": process.env.VUE_APP_X_API_KEY,
              Authorization: `Bearer ${token}`,
            },
          });

          const usernames = res.data?.giveawayUsers?.map((user) => user.user.username);
          if (usernames?.includes(username)) {
            this.joinedGiveawayIds.add(giveawayId);
          }
        } catch (err) {
          console.error("Error checking if joined:", err);
        }
      }
    },
    showSuccess(ticket) {
      this.ticket = ticket;
      this.showSuccessModal = true;
    },
    showError(message) {
      this.errorMessage = message;
      this.showErrorModal = true;
    },
    animateValues() {
      this.animateNumber("animatedPoints", this.points);
      this.animateNumber("animatedTotalGiveaway", this.totalGiveaway);
    },
    animateNumber(variable, targetValue) {
      let current = this[variable];

      // Fix: Strip commas and parse float safely
      let startValue = parseFloat(String(current).replace(/,/g, "")) || 0;

      if (startValue === targetValue) return;

      const step = Math.max(1, Math.abs(targetValue - startValue) / 20);
      const isNegative = startValue > targetValue;

      const interval = setInterval(() => {
        if (isNegative) {
          startValue -= step;
          if (startValue <= targetValue) {
            startValue = targetValue;
            clearInterval(interval);
          }
        } else {
          startValue += step;
          if (startValue >= targetValue) {
            startValue = targetValue;
            clearInterval(interval);
          }
        }

        this[variable] = Number(startValue).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }, 50);
    },
    toggleDesktopFilter() {
      this.showDesktopFilter = !this.showDesktopFilter;
    },
    toggleMobileFilter() {
      this.showMobileFilter = !this.showMobileFilter;
    },
    applyFilter() {
      this.currentPage = 1;
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    toggleSortDropdown() {
      this.showSortDropdown = !this.showSortDropdown;
    },
    toggleCategoryDropdown() {
      this.showCategoryDropdown = !this.showCategoryDropdown;
    },
    applySort(option) {
      this.selectedSortOption = option.label;
      this.sortProducts(option.value);
      this.showSortDropdown = false;
    },
    applyCategory(category) {
      this.selectedCategory = category;
      this.showCategoryDropdown = false;
      this.currentPage = 1;
    },
    sortProducts(order) {
      if (order === "aToZ") {
        this.giveaways.sort((a, b) => a.title.localeCompare(b.title));
      } else if (order === "zToA") {
        this.giveaways.sort((a, b) => b.title.localeCompare(a.title));
      }
    },
    openGiveawayDetailsModal(giveaway) {
      this.giveawayDetails = giveaway;
      this.showGiveawayConfirmationModal = false;
      this.showGiveawayDetailsModal = true;
    },
    handleGiveawayClick(giveaway, hasJoined) {
      if (giveaway.status === "started" && hasJoined !== true) {
        this.showGiveawayConfirmationModal = true;
        this.giveawayDetails = giveaway;
      } else {
        this.openGiveawayDetailsModal(giveaway);
      }
    },
    handleJoinSuccess() {
      this.refreshData();
      this.joinedGiveawayTitle = this.giveawayDetails.title;
      this.showGiveawaySuccessModal = true;
    },
  },
  mounted() {
    this.init();
    this.fetchGiveaways();
  },
};
</script>
